<template>
  <div
    class="cover-studio"
  >
    <div class="cover-studio-grid">
      <div class="cover-stage">
        <div class="cover-stage-label">
          <span>{{ $t('cover-studio.live-preview') }}</span><span>3000 × 3000</span>
        </div>
        <canvas
          ref="preview"
          width="700"
          height="700"
          class="cover-live-canvas"
          role="img"
          :aria-label="$t('cover-studio.preview-label', { title: title || $t('cover-studio.your-title') })"
        />
        <div class="cover-listening-preview">
          <canvas
            ref="miniature"
            width="80"
            height="80"
            aria-hidden="true"
          />
          <div><strong>{{ title || $t('cover-studio.your-title') }}</strong><span>{{ artist || $t('cover-studio.your-name') }}</span></div>
          <span class="cover-size-note">{{ $t('cover-studio.small-preview') }}</span>
        </div>
        <p class="cover-preview-note">
          {{ $t('cover-studio.preview-note') }}
        </p>
      </div>

      <div class="cover-controls">
        <fieldset
          class="cover-control-group"
          :disabled="busy || generating"
        >
          <legend><span>01</span>{{ $t('cover-studio.choose-design') }}</legend>
          <div class="cover-design-grid">
            <button
              v-for="design in coverDesigns"
              :key="design.id"
              type="button"
              class="cover-design"
              :class="{ selected: selectedDesign === design.id }"
              :aria-pressed="selectedDesign === design.id"
              @click="selectedDesign = design.id"
            >
              <canvas
                :ref="el => setThumbnail(design.id, el)"
                width="180"
                height="180"
                aria-hidden="true"
              />
              <span>{{ $t(`cover-studio.design-${design.id}`) }}<small v-if="design.premium">PRO</small></span>
            </button>
          </div>
        </fieldset>
        <fieldset
          class="cover-control-group"
          :disabled="busy || generating"
        >
          <legend><span>02</span>{{ $t('cover-studio.set-mood') }}</legend>
          <div class="cover-palette-grid">
            <button
              v-for="palette in coverPalettes"
              :key="palette.id"
              type="button"
              class="cover-palette"
              :class="{ selected: selectedPalette === palette.id }"
              :aria-pressed="selectedPalette === palette.id"
              :aria-label="$t(`cover-studio.palette-${palette.id}`)"
              :title="$t(`cover-studio.palette-${palette.id}`)"
              @click="applyPalette(palette)"
            >
              <span :style="{ background: palette.background }" /><span :style="{ background: palette.primary }" /><span :style="{ background: palette.accent }" />
            </button>
          </div>
          <div class="cover-palette-caption">
            <span>{{ $t(selectedPalette ? `cover-studio.palette-${selectedPalette}` : 'cover-studio.custom') }}</span><button
              type="button"
              class="cover-text-button"
              @click="variant++"
            >
              ↻ {{ $t('cover-studio.remix') }}
            </button>
          </div>
        </fieldset>
        <fieldset
          class="cover-control-group"
          :disabled="busy || generating"
        >
          <legend><span>03</span>{{ $t('cover-studio.make-yours') }}</legend>
          <label class="cover-checkbox"><input
            v-model="showText"
            type="checkbox"
          >{{ $t('cover-studio.show-text') }}</label>
          <div
            v-if="showText && selectedDesign !== 'type'"
            class="cover-position"
            role="group"
            :aria-label="$t('cover-studio.text-placement')"
          >
            <button
              v-for="placement in ['bottom', 'top']"
              :key="placement"
              type="button"
              :aria-pressed="position === placement"
              @click="position = placement"
            >
              {{ $t(`cover-studio.${placement}`) }}
            </button>
          </div>
          <details class="cover-custom-colors">
            <summary>{{ $t('cover-studio.fine-tune') }}</summary>
            <div class="cover-color-grid">
              <label
                v-for="key in ['background', 'primary', 'accent', 'text']"
                :key="key"
              ><input
                v-model="colors[key]"
                type="color"
                @input="selectedPalette = null"
              ><span>{{ $t(`cover-studio.color-${key}`) }}</span></label>
            </div>
          </details>
        </fieldset>
      </div>
    </div>
    <div class="cover-studio-footer">
      <div><strong>{{ $t(isLocked ? 'cover-studio.pro-title' : 'cover-studio.ready-title') }}</strong><p>{{ $t(isLocked ? 'cover-studio.pro-copy' : 'cover-studio.export-copy') }}</p></div>
      <button
        type="button"
        class="cover-apply"
        :disabled="busy || generating || (!isLocked && missingDetails)"
        :aria-busy="busy || generating"
        @click="useCover"
      >
        <span
          v-if="busy || generating"
          class="cover-button-spinner"
          aria-hidden="true"
        />
        {{ $t(busy || generating ? `cover-studio.${activityPhase}` : isLocked ? 'cover-studio.unlock' : 'cover-studio.use-cover') }}
        <span
          v-if="!busy && !generating"
          aria-hidden="true"
        >↗</span>
      </button>
    </div>
    <CoverActivity
      v-if="busy || generating"
      :phase="activityPhase"
      :progress="uploadProgress"
    />
    <p
      v-if="missingDetails"
      class="cover-inline-note"
    >
      {{ $t('cover-studio.missing-details') }}
    </p>
    <p
      v-if="error"
      role="alert"
      class="cover-error"
    >
      {{ error }}
    </p>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { coverDesigns, coverPalettes, drawCover, exportCover, normalizeDesign, waitForCoverPaint } from '@/utils/coverStudio';
import CoverActivity from './CoverActivity.vue';
const props = defineProps({
  title: { type: String, default: '' },
  artist: { type: String, default: '' },
  initialTemplate: { type: String, default: 'orbit' },
  isPremium: Boolean,
  busy: Boolean,
  uploadProgress: { type: Number, default: null },
});
const emit = defineEmits(['update:artwork', 'upgrade', 'preparing']);
const { t, locale } = useI18n();
const preview = ref(null);
const miniature = ref(null);
const thumbnails = new Map();
const selectedDesign = ref(normalizeDesign(props.initialTemplate));
const selectedPalette = ref(coverPalettes[0].id);
const colors = reactive({ ...coverPalettes[0] });
const variant = ref(0);
const position = ref('bottom');
const showText = ref(true);
const generating = ref(false);
const error = ref('');
const activityPhase = computed(() => !props.busy ? 'preparing' : props.uploadProgress >= 100 ? 'processing' : 'uploading');
let disposed = false;
const isLocked = computed(() => !props.isPremium && coverDesigns.find(design => design.id === selectedDesign.value)?.premium);
const missingDetails = computed(() => showText.value && (!props.title.trim() || !props.artist.trim()));
const options = computed(() => ({
  design: selectedDesign.value, colors: { ...colors }, variant: variant.value,
  title: props.title || t('cover-studio.your-title'),
  artist: props.artist || t('cover-studio.your-name'),
  showText: showText.value, position: position.value,
}));
function setThumbnail(id, element) { if (element) thumbnails.set(id, element); else thumbnails.delete(id); }
function applyPalette(palette) { Object.assign(colors, palette); selectedPalette.value = palette.id; }
async function render() {
  await nextTick();
  if (disposed) return;
  try {
    if (preview.value) drawCover(preview.value, options.value);
    if (miniature.value) drawCover(miniature.value, options.value);
    for (const [design, canvas] of thumbnails) drawCover(canvas, { ...options.value, design, showText: false });
  } catch { error.value = t('cover-studio.render-error'); }
}
async function useCover() {
  if (props.busy || generating.value) return;
  if (isLocked.value) { emit('upgrade'); return; }
  if (missingDetails.value) return;
  generating.value = true; error.value = '';
  const selectedOptions = options.value;
  emit('preparing', true);
  try {
    await nextTick();
    await waitForCoverPaint();
    if (disposed) return;
    const artwork = await exportCover(selectedOptions);
    if (!disposed) emit('update:artwork', artwork);
  }
  catch { error.value = t('cover-studio.export-error'); }
  finally { generating.value = false; emit('preparing', false); }
}
watch([options, locale], render, { deep: true });
watch(() => props.initialTemplate, value => { selectedDesign.value = normalizeDesign(value); });
onMounted(async () => { await render(); if (document.fonts?.ready) { await document.fonts.ready; await render(); } });
onBeforeUnmount(() => { disposed = true; });
</script>

<style scoped>
@reference '../../../../styles/refresh.css';
.cover-studio { container-type:inline-size;color:var(--growth-ink,#20221f); }
.cover-studio-grid { @apply tw:grid tw:gap-7; }
.cover-stage { min-width:0; }
.cover-stage-label { @apply tw:flex tw:justify-between tw:gap-3; font-size:9px;letter-spacing:.1em;text-transform:uppercase;color:var(--growth-muted,#65705b);margin-bottom:12px; }
.cover-live-canvas { width:100%;aspect-ratio:1;display:block;border-radius:12px;box-shadow:0 18px 40px #0002; }
.cover-listening-preview { display:flex;gap:10px;align-items:center;padding:15px 0 12px;border-bottom:1px solid var(--growth-line,#dce0d4); }
.cover-listening-preview canvas { width:38px;height:38px;border-radius:5px;flex-shrink:0; }
.cover-listening-preview>div { min-width:0;flex:1; }
.cover-listening-preview strong,.cover-listening-preview div span { display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:11px; }
.cover-listening-preview div span { color:var(--growth-muted,#65705b);font-size:10px;margin-top:2px; }
.cover-size-note { font-size:9px;color:var(--growth-muted,#65705b);max-width:70px;text-align:right; }
.cover-preview-note { color:var(--growth-muted,#65705b);font-size:10px;line-height:1.6;margin-top:12px; }
.cover-controls { @apply tw:grid tw:gap-6;align-content:start;min-width:0; }
.cover-control-group { border:0;min-width:0;padding:0;margin:0; }
.cover-control-group legend { font-size:12px;font-weight:600;margin-bottom:12px;color:var(--growth-ink,#20221f); }
.cover-control-group legend>span { color:var(--growth-muted,#65705b);font-size:9px;margin-right:10px; }
.cover-design-grid { display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:8px; }
.cover-design { background:transparent;color:inherit;border:1px solid var(--growth-line,#dce0d4);border-radius:9px;padding:4px;cursor:pointer;min-width:0; }
.cover-design.selected { border-color:var(--growth-ink,#20221f);box-shadow:0 0 0 1px var(--growth-ink,#20221f); }
.cover-design canvas { display:block;width:100%;aspect-ratio:1;border-radius:5px; }
.cover-design>span { display:flex;justify-content:space-between;flex-wrap:wrap;gap:2px;font-size:9px;padding:7px 1px 3px; }
.cover-design small { font-size:7px;color:var(--growth-muted,#65705b); }
.cover-palette-grid { display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:8px; }
.cover-palette { display:flex;border:2px solid transparent;outline:1px solid var(--growth-line,#dce0d4);padding:3px;border-radius:50%;background:var(--growth-card,#fff);aspect-ratio:1;overflow:hidden;cursor:pointer;min-height:34px; }
.cover-palette.selected { outline:2px solid var(--growth-ink,#20221f); }
.cover-palette span { flex:1; }
.cover-palette span:first-child { border-radius:20px 0 0 20px; }
.cover-palette span:last-child { border-radius:0 20px 20px 0; }
.cover-palette-caption { display:flex;align-items:center;justify-content:space-between;gap:8px;font-size:10px;color:var(--growth-muted,#65705b);margin-top:9px; }
.cover-text-button { background:transparent;border:0;min-height:34px;color:var(--growth-ink,#20221f);cursor:pointer;font-size:10px; }
.cover-checkbox { display:flex;align-items:center;gap:10px;font-size:11px;min-height:40px;cursor:pointer; }
.cover-checkbox input { accent-color:var(--color-primary,#53643c);width:16px;height:16px; }
.cover-position { display:flex;gap:6px;margin:8px 0 12px; }
.cover-position button { flex:1;border:1px solid var(--growth-line,#dce0d4);border-radius:7px;background:transparent;color:inherit;padding:9px;font-size:10px;cursor:pointer; }
.cover-position button[aria-pressed=true] { background:var(--growth-soft,#e8eddc);border-color:var(--growth-muted,#65705b); }
.cover-custom-colors { border-top:1px solid var(--growth-line,#dce0d4);margin-top:8px; }
.cover-custom-colors summary { cursor:pointer;font-size:11px;padding:13px 0; }
.cover-color-grid { display:grid;grid-template-columns:1fr 1fr;gap:12px; }
.cover-color-grid label { display:flex;align-items:center;gap:8px;font-size:10px; }
.cover-color-grid input { padding:2px;width:32px;height:32px;background:var(--growth-paper,#f6f7f1);border:1px solid var(--growth-line,#dce0d4);border-radius:7px; }
.cover-studio-footer { display:flex;justify-content:space-between;align-items:center;gap:20px;border-top:1px solid var(--growth-line,#dce0d4);margin-top:24px;padding-top:20px; }
.cover-studio-footer strong { font-size:12px;color:inherit; }
.cover-studio-footer p,.cover-inline-note { font-size:10px;color:var(--growth-muted,#65705b);line-height:1.7;margin-top:4px; }
.cover-apply { display:flex;gap:18px;justify-content:space-between;align-items:center;flex-shrink:0;border:1px solid #d5fc67;border-radius:10px;background:#d5fc67;color:#20221f;min-height:46px;padding:12px 18px;font-size:12px;font-weight:600;cursor:pointer; }
.cover-apply:disabled { opacity:.5;cursor:default; }
.cover-apply[aria-busy=true] { opacity:1;cursor:progress; }
.cover-button-spinner { width:16px;height:16px;flex-shrink:0;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;animation:cover-button-spin .8s linear infinite; }
@keyframes cover-button-spin { to { transform:rotate(360deg); } }
@media(prefers-reduced-motion:reduce) { .cover-button-spinner { animation:none; } }
.cover-error { color:var(--color-danger,#a8342e);font-size:12px;margin-top:12px; }
.cover-studio :is(button,input,summary):focus-visible { outline:3px solid var(--color-primary,#53643c);outline-offset:4px; }
@container (min-width:600px) { .cover-studio-grid { grid-template-columns:minmax(0,1.05fr) minmax(0,1fr);gap:28px; }.cover-stage { align-self:start;position:sticky;top:100px; } }
@container (max-width:420px) { .cover-studio-footer { align-items:stretch;flex-direction:column;gap:14px; }.cover-live-canvas { max-width:360px;margin:auto; }.cover-design>span {font-size:10px} }
</style>
